import { ChatMessageType, GameState } from "./types";
import { PLAYER_COLOR_HEX } from "./colorPalette";

export interface ChatController {
  addMessage: (text: string, type: ChatMessageType, options?: ChatMessageOptions) => void;
  setPrompt: (prompt: string | null) => void;
  setInputEnabled: (enabled: boolean) => void;
  updateSkipVoteButton: (isVotingPhase: boolean, enabled: boolean) => void;
  addPlayerMessage: (text: string, playerName?: string, color?: string) => void;
  updateActionButtons: () => void;
}

type SendActionHandler = (message: string) => Promise<void>;

export interface ChatMessageOptions {
  playerName?: string;
  color?: string;
  isAlive?: boolean;
  isHuman?: boolean;
  timestamp?: string;
}

export function handleChat(
  gameState: GameState,
  sendAction: SendActionHandler
): ChatController {
  const chatInput = document.getElementById("chat-input") as HTMLInputElement | null;
  const sendButton = document.getElementById("send-button") as HTMLButtonElement | null;
  const skipVoteButton = document.getElementById("skip-vote-button") as HTMLButtonElement | null;
  const chatContainer = document.getElementById("chat-container") as HTMLDivElement | null;
  const uiContainer = document.getElementById("ui-container") as HTMLDivElement | null;
  const actionButtonsContainer = document.getElementById("action-buttons-container") as HTMLDivElement | null;

  if (!chatInput || !sendButton || !chatContainer || !actionButtonsContainer) {
    console.error("Chat UI elements could not be initialised.");
    return {
      addMessage: () => {},
      setPrompt: () => {},
      setInputEnabled: () => {},
      updateSkipVoteButton: () => {},
      addPlayerMessage: () => {},
      updateActionButtons: () => {},
    };
  }

  // Helper function to get hex color from color name
  function getColorForName(colorName: string): string {
    const hex = PLAYER_COLOR_HEX[colorName as keyof typeof PLAYER_COLOR_HEX];
    console.log("[DEBUG] getColorForName:", { colorName, hex, result: hex || "#888888" });
    return hex || "#888888";
  }

  const promptElement = ensurePromptElement(uiContainer, chatContainer);
  let isSubmitting = false;
  let shouldAutoScroll = true; // Enable auto-scroll by default

  // Track if user scrolled manually
  chatContainer.addEventListener("scroll", () => {
    // User scrolled to bottom? Enable auto-scroll for NEXT message
    const isAtBottom = chatContainer.scrollHeight - chatContainer.scrollTop - chatContainer.clientHeight < 50;
    shouldAutoScroll = isAtBottom;
  });

  // Enable input by default
  chatInput.disabled = false;
  sendButton.disabled = false;

  // Track input enabled state to avoid unnecessary focus() calls
  let wasInputEnabled = false;

  // Track action buttons state to avoid unnecessary rebuilds
  let lastActionButtonsSignature = "";

  function addMessage(
    text: string,
    type: ChatMessageType,
    options?: ChatMessageOptions
  ): void {
    if (!chatContainer) {
      return;
    }
    const messageElement = createMessageElement(text, type, options);
    chatContainer.appendChild(messageElement);

    // Only auto-scroll if user has scrolled to bottom (shouldAutoScroll=true)
    if (shouldAutoScroll) {
      // Use requestAnimationFrame to avoid blocking DOM operations
      requestAnimationFrame(() => {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      });
    }
  }

  const setPrompt = (prompt: string | null) => {
    if (!promptElement) {
      return;
    }
    // Don't show the full AI prompt - it's too verbose for players
    promptElement.textContent = prompt && prompt.trim()
      ? prompt
      : "Waiting for the next action...";
  };

  const setInputEnabled = (enabled: boolean) => {
    chatInput.disabled = !enabled;
    sendButton.disabled = !enabled;
    
    // Only focus when transitioning from disabled to enabled
    // This prevents stealing focus from dropdowns during repeated calls
    if (enabled && !wasInputEnabled) {
      chatInput.focus();
    }
    wasInputEnabled = enabled;
  };

  const updateSkipVoteButton = (isVotingPhase: boolean, enabled: boolean) => {
    if (!skipVoteButton) return;
    
    if (isVotingPhase) {
      skipVoteButton.classList.remove("hidden");
      skipVoteButton.disabled = !enabled;
    } else {
      skipVoteButton.classList.add("hidden");
      skipVoteButton.disabled = true;
    }
  };

  // Skip vote button handler
  if (skipVoteButton) {
    skipVoteButton.addEventListener("click", (event) => {
      event.preventDefault();
      void submitSkipVote();
    });
  }

  async function submitSkipVote(): Promise<void> {
    if (isSubmitting || !chatInput) {
      return;
    }

    const pendingAction = gameState.pendingAction;
    if (!pendingAction) {
      addMessage(
        "No action is pending right now. Please wait for the next prompt.",
        "system"
      );
      return;
    }

    isSubmitting = true;
    chatInput.value = "";

    addPlayerMessage(
      "ABSTAIN",
      gameState.humanPlayerName || "You",
      gameState.humanPlayerColor ? getColorForName(gameState.humanPlayerColor) : undefined
    );
    setInputEnabled(false);
    updateSkipVoteButton(true, false);

    try {
      await sendAction("ABSTAIN");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit abstain. Please try again.";
      addMessage(errorMessage, "system");
      setInputEnabled(true);
      updateSkipVoteButton(true, true);
    } finally {
      isSubmitting = false;
    }
  }

  async function submitCurrentMessage(): Promise<void> {
    if (isSubmitting || !chatInput) {
      return;
    }

    const pendingAction = gameState.pendingAction;
    if (!pendingAction) {
      addMessage(
        "No action is pending right now. Please wait for the next prompt.",
        "system"
      );
      return;
    }

    const message = chatInput.value.trim();
    if (!message) {
      return;
    }

    isSubmitting = true;
    const previousValue = chatInput.value;
    chatInput.value = "";

    addPlayerMessage(
      message,
      gameState.humanPlayerName || "You",
      gameState.humanPlayerColor ? getColorForName(gameState.humanPlayerColor) : undefined
    );
    setInputEnabled(false);

    try {
      await sendAction(message);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit action. Please try again.";
      addMessage(errorMessage, "system");
      chatInput.value = previousValue;
      setInputEnabled(true);
    } finally {
      isSubmitting = false;
    }
  }

  sendButton.addEventListener("click", (event) => {
    event.preventDefault();
    void submitCurrentMessage();
  });

  chatInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submitCurrentMessage();
    }
  });

  setInputEnabled(false);
  setPrompt(null);

  const addPlayerMessage = (text: string, playerName?: string, color?: string) => {
    console.log("[DEBUG] addPlayerMessage called with:", { text, playerName, color, humanPlayerColor: gameState.humanPlayerColor });
    addMessage(text, "player", {
      playerName: playerName || "You",
      color: color || "#888888",
    });
  };

  const updateActionButtons = () => {
    if (!actionButtonsContainer) return;

    const pendingAction = gameState.pendingAction;
    if (!pendingAction || !pendingAction.metadata) {
      actionButtonsContainer.classList.add("hidden");
      return;
    }

    const phase = pendingAction.metadata.phase || pendingAction.metadata.phase_type || "";
    const playerRole = gameState.playerRole;
    const availablePlayers = pendingAction.metadata.available_players || [];
    
    // Create a signature of current state to detect if rebuild is needed
    const currentSignature = JSON.stringify({
      phase,
      playerRole,
      humanPlayerName: gameState.humanPlayerName,
      alivePlayers: availablePlayers.filter(p => p.alive).map(p => p.player_name).sort()
    });

    // Only rebuild if something changed
    if (currentSignature === lastActionButtonsSignature) {
      return;
    }
    lastActionButtonsSignature = currentSignature;

    // Clear existing buttons
    actionButtonsContainer.innerHTML = "";
    
    // Filter out the human player and dead players
    const alivePlayers = availablePlayers.filter((p) => 
      p.alive && p.player_name !== gameState.humanPlayerName
    );

    let shouldShow = false;

    // NIGHT PHASE: Mafia Kill Action
    if (phase.toLowerCase().includes("night") && playerRole === "MAFIA") {
      // Filter out other mafia members
      const targets = alivePlayers.filter((p) => p.role !== "MAFIA");
      if (targets.length > 0) {
        shouldShow = true;
        const row = createActionButtonRow(
          "🔪 Mafia Kill:",
          targets,
          "Kill",
          "mafia-kill",
          (playerName) => {
            chatInput.value = `KILL: ${playerName}`;
          }
        );
        actionButtonsContainer.appendChild(row);
      }
    }

    // NIGHT PHASE: Doctor Protect Action
    if (phase.toLowerCase().includes("night") && playerRole === "DOCTOR") {
      // Doctor can protect anyone including themselves
      const targets = [...availablePlayers.filter((p) => p.alive)];
      // Add self to targets
      if (gameState.humanPlayerName && !targets.find(p => p.player_name === gameState.humanPlayerName)) {
        targets.push({
          player_name: gameState.humanPlayerName,
          alive: true,
        } as any);
      }
      if (targets.length > 0) {
        shouldShow = true;
        const row = createActionButtonRow(
          "🛡️ Protect:",
          targets,
          "Protect",
          "doctor-protect",
          (playerName) => {
            chatInput.value = `PROTECT: ${playerName}`;
          }
        );
        actionButtonsContainer.appendChild(row);
      }
    }

    // NIGHT PHASE: Sheriff Investigate Action
    if (phase.toLowerCase().includes("night") && playerRole === "SHERIFF") {
      // Sheriff can investigate anyone except themselves
      const targets = alivePlayers;
      if (targets.length > 0) {
        shouldShow = true;
        const row = createActionButtonRow(
          "🔍 Investigate:",
          targets,
          "Investigate",
          "sheriff-investigate",
          (playerName) => {
            chatInput.value = `CHECK: ${playerName}`;
          }
        );
        actionButtonsContainer.appendChild(row);
      }
    }

    // DAY PHASE: Vote Action (available to all players during voting)
    if ((phase.toLowerCase().includes("day") || phase.toLowerCase().includes("voting")) && 
        alivePlayers.length > 0) {
      shouldShow = true;
      // Add ABSTAIN option to vote targets
      const voteTargets = [
        { player_name: "ABSTAIN", alive: true } as any,
        ...alivePlayers
      ];
      const row = createActionButtonRow(
        "🗳️ Vote:",
        voteTargets,
        "Vote",
        "vote",
        (playerName) => {
          const currentMessage = chatInput.value.trim();
          const voteCommand = `VOTE: ${playerName}`;
          
          if (currentMessage) {
            // Append vote to existing message
            chatInput.value = `${currentMessage}\n${voteCommand}`;
          } else {
            // Just the vote command
            chatInput.value = voteCommand;
          }
        }
      );
      actionButtonsContainer.appendChild(row);
    }

    if (shouldShow) {
      actionButtonsContainer.classList.remove("hidden");
    } else {
      actionButtonsContainer.classList.add("hidden");
    }
  };

  function createActionButtonRow(
    label: string,
    players: any[],
    actionType: string,
    buttonClass: string,
    onSelect: (playerName: string) => void
  ): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "action-button-row";

    const labelEl = document.createElement("div");
    labelEl.className = "action-button-label";
    labelEl.textContent = label;
    row.appendChild(labelEl);

    const select = document.createElement("select");
    select.className = "player-select";
    
    const defaultOption = document.createElement("option");
    defaultOption.value = "";
    defaultOption.textContent = `Select player...`;
    select.appendChild(defaultOption);

    players.forEach((player) => {
      const option = document.createElement("option");
      option.value = player.player_name || player.name || "";
      option.textContent = player.player_name || player.name || "";
      select.appendChild(option);
    });

    const button = document.createElement("button");
    button.className = `action-button ${buttonClass}`;
    button.textContent = actionType;
    button.disabled = true;

    select.addEventListener("change", () => {
      button.disabled = !select.value;
    });

    // Prevent autofocus on chat input when using dropdowns
    select.addEventListener("mousedown", (e) => {
      e.stopPropagation();
    });
    select.addEventListener("focus", () => {
      if (chatInput) chatInput.blur();
    });

    button.addEventListener("click", () => {
      if (select.value) {
        onSelect(select.value);
      }
    });

    row.appendChild(select);
    row.appendChild(button);

    return row;
  }

  return {
    addMessage,
    setPrompt,
    setInputEnabled,
    updateSkipVoteButton,
    addPlayerMessage,
    updateActionButtons,
  };
}

function createMessageElement(
  text: string,
  type: ChatMessageType,
  options?: ChatMessageOptions
): HTMLDivElement {
  const element = document.createElement("div");
  element.style.padding = "12px 16px";
  element.style.marginBottom = "8px";
  element.style.borderRadius = "12px";
  element.style.wordWrap = "break-word";
  element.style.whiteSpace = "pre-wrap";
  element.style.position = "relative";
  element.style.overflow = "hidden";
  element.style.cursor = "pointer";
  element.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
  element.style.transform = "translateY(0)";
  element.style.width = "100%";

  // Get label and color
  const label = options?.playerName || defaultLabelForType(type);
  const color = options?.color || "#888888";

  console.log("[DEBUG] createMessageElement:", { label, color, options });

  // Glassmorphism effect with gradient
  element.style.background = `linear-gradient(135deg, ${color}dd 0%, ${color}bb 100%)`;
  element.style.backdropFilter = "blur(10px)";
  element.style.boxShadow = `0 8px 32px ${color}40, 0 2px 8px ${color}20`;
  element.style.border = `1px solid ${color}60`;

  // Animated accent bar on the left
  const accentBar = document.createElement("div");
  accentBar.style.position = "absolute";
  accentBar.style.left = "0";
  accentBar.style.top = "0";
  accentBar.style.bottom = "0";
  accentBar.style.width = "4px";
  accentBar.style.background = `linear-gradient(180deg, ${color} 0%, ${color}80 100%)`;
  accentBar.style.transition = "width 0.3s ease";
  element.appendChild(accentBar);

  // Build content with better hierarchy
  const labelElement = document.createElement("strong");
  labelElement.textContent = label;
  labelElement.style.fontSize = "0.95em";
  labelElement.style.display = "block";
  labelElement.style.color = "#ffffff";
  labelElement.style.textShadow = "0 1px 3px rgba(0,0,0,0.3)";
  labelElement.style.letterSpacing = "0.3px";
  labelElement.style.marginBottom = "4px";

  const textElement = document.createElement("div");
  textElement.style.fontSize = "0.9em";
  textElement.style.lineHeight = "1.5";
  textElement.style.color = "#ffffff";
  textElement.style.textShadow = "0 1px 2px rgba(0,0,0,0.2)";
  textElement.textContent = text;

  element.appendChild(labelElement);
  element.appendChild(textElement);

  // Hover effects
  element.addEventListener("mouseenter", () => {
    element.style.transform = "translateY(-2px)";
    element.style.boxShadow = `0 12px 40px ${color}50, 0 4px 12px ${color}30`;
    accentBar.style.width = "6px";
  });

  element.addEventListener("mouseleave", () => {
    element.style.transform = "translateY(0)";
    element.style.boxShadow = `0 8px 32px ${color}40, 0 2px 8px ${color}20`;
    accentBar.style.width = "4px";
  });

  // Click to copy with flash effect
  element.addEventListener("click", async () => {
    const messageText = `${label}\n${text}`;
    try {
      await navigator.clipboard.writeText(messageText);
      
      // Flash effect
      const flash = document.createElement("div");
      flash.style.position = "absolute";
      flash.style.top = "0";
      flash.style.left = "0";
      flash.style.right = "0";
      flash.style.bottom = "0";
      flash.style.background = "rgba(255, 255, 255, 0.3)";
      flash.style.pointerEvents = "none";
      flash.style.opacity = "1";
      flash.style.transition = "opacity 0.4s ease";
      element.appendChild(flash);
      
      setTimeout(() => {
        flash.style.opacity = "0";
        setTimeout(() => flash.remove(), 400);
      }, 100);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  });

  // Entry animation
  element.style.opacity = "0";
  element.style.transform = "translateY(20px)";
  requestAnimationFrame(() => {
    element.style.opacity = "1";
    element.style.transform = "translateY(0)";
  });

  return element;
}

function ensurePromptElement(
  uiContainer: HTMLDivElement | null,
  chatContainer: HTMLDivElement
): HTMLDivElement | null {
  let promptElement = document.getElementById("action-prompt") as HTMLDivElement | null;
  if (promptElement) {
    return promptElement;
  }

  promptElement = document.createElement("div");
  promptElement.id = "action-prompt";
  promptElement.style.marginBottom = "10px";
  promptElement.style.fontWeight = "bold";
  promptElement.style.whiteSpace = "pre-wrap";

  if (uiContainer) {
    uiContainer.insertBefore(promptElement, chatContainer);
  } else if (chatContainer.parentElement) {
    chatContainer.parentElement.insertBefore(promptElement, chatContainer);
  }

  return promptElement;
}

function defaultLabelForType(type: ChatMessageType): string {
  switch (type) {
    case "player":
      return "You";
    case "llm":
      return "LLM Player";
    default:
      return "System";
  }
}

function applyMessageColor(element: HTMLDivElement, hexColor: string, type: ChatMessageType) {
  // Use transparent background
  element.style.backgroundColor = "transparent";
  // Use player color for the left border
  element.style.borderColor = hexColor;
  // Use pure black text for readability
  element.style.color = "#000000";
}

function hexToRgba(hexColor: string, alpha: number): string {
  const sanitized = hexColor.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const hasShortFormat = sanitized.length === 3;

  if (Number.isNaN(bigint)) {
    return `rgba(255, 255, 255, ${alpha})`;
  }

  const r = hasShortFormat ? ((bigint >> 8) & 0xf) * 17 : (bigint >> 16) & 0xff;
  const g = hasShortFormat ? ((bigint >> 4) & 0xf) * 17 : (bigint >> 8) & 0xff;
  const b = hasShortFormat ? (bigint & 0xf) * 17 : bigint & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getContrastingTextColor(hexColor: string): string {
  const sanitized = hexColor.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const hasShortFormat = sanitized.length === 3;

  if (Number.isNaN(bigint)) {
    return "#ffffff";
  }

  const r = hasShortFormat ? ((bigint >> 8) & 0xf) * 17 : (bigint >> 16) & 0xff;
  const g = hasShortFormat ? ((bigint >> 4) & 0xf) * 17 : (bigint >> 8) & 0xff;
  const b = hasShortFormat ? (bigint & 0xf) * 17 : bigint & 0xff;

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.65 ? "#111111" : "#ffffff";
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
